import React, {useRef, useEffect} from 'react';
import Image from 'next/image';
import styles from './personal-plan-module.module.scss';
import {useI18n} from '@udemy-v2/i18n';
import classNames from 'classnames';
import {publishEvent} from '@udemy-v2/event-tracking';
import {SubscriptionOfferPresented, SubscriptionLearnMoreEvent} from '../events';

interface PersonalPlanModuleProps {
    planPriceId?: string;
    planPriceIds?: number[];
}

export const PersonalPlanModule = ({
    planPriceId = '287615',
    planPriceIds = [287615, 287617],
}: PersonalPlanModuleProps) => {
    const {gettext} = useI18n();
    const moduleRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLAnchorElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        observer.disconnect();

                        publishEvent(
                            new SubscriptionOfferPresented({
                                uiRegion: 'take_your_hustle_next_level',
                                primaryPricingOption: {
                                    price: '29.99',
                                    listPrice: '29.99',
                                    currency: 'USD',
                                    renewalIntervalType: 'month',
                                    renewalIntervalCount: 1,
                                    hasTrial: true,
                                    trialDays: 7,
                                },
                                countryCode: 'US',
                                pageFor: '60dayhustle',
                            }),
                        );
                    }
                });
            },
            {threshold: 0.5},
        );

        if (moduleRef.current) {
            observer.observe(moduleRef.current);
        }

        return () => {
            observer.disconnect();
        };
    }, []);

    const handleButtonClick = () => {
        publishEvent(
            new SubscriptionLearnMoreEvent({
                planPriceId,
                planType: 'CONSUMERSUBSCRIPTION',
                uiRegion: 'take_your_hustle_next_level',
                planPriceIds,
                pageFor: '60dayhustle',
            }),
        );
    };

    return (
        <div ref={moduleRef} className={styles['pp-module']}>
            <Image
                src="/60-day-hustle/images/hero.png"
                alt="Hero image"
                width={800}
                height={600}
                priority
                className={styles['hero-image']}
            />

            <div className={styles['pp-info-module']}>
                <div className={classNames('ud-heading-xxl', styles['pp-info-module-title'])}>
                    {gettext('Take your hustle next level')}
                </div>
                <div className={classNames('ud-text-md', styles['pp-info-module-description'])}>
                    {gettext(
                        'Go further in work and in life with subscription access to a collection of top-rated courses in tech, business, and other cutting-edge skills.',
                    )}
                </div>
                <a
                    ref={buttonRef}
                    className={styles['pp-info-module-button']}
                    href="https://www.udemy.com/personal-plan/"
                    onClick={handleButtonClick}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <div
                        className={classNames(
                            'ud-text-sm',
                            'ud-text-bold',
                            styles['pp-info-module-button-text'],
                        )}
                    >
                        {gettext('Get Udemy Personal Plan')}
                    </div>
                </a>
            </div>
        </div>
    );
};
